import { AllMapLayers, MapLayer, MapLayers } from './mapping.constants';

export enum MapType {
  Main = 'main',
  Timeline = 'timeline',
  GenericForm = 'genericForm',
  PlaceForm = 'placeForm',
  FirePerimeterForm = 'firePerimeterForm',
  MediaForm = 'mediaForm',
  IncidentForm = 'incidentForm',
  FloodingForm = 'floodingForm',
  ReportForm = 'reportForm',
  FloodingReportForm = 'floodingReportForm',
  AutomatedGeoEventForm = 'automatedGeoEventForm',
  CameraDetails = 'cameraDetails',
  GeoEventEmbed = 'geoEventEmbed',
  ReportDetail = 'reportDetail',
  EditReportForm = 'editReportForm',
}

export type MapTypePolicy = {
  allowedLayers: MapLayer[];
  allowMainControls: boolean;
  allowToolsControl: boolean;
  allowZoomControl: boolean;
  allowGeolocateControl: boolean;
  allowLayersControl: boolean;
  allowTimelineControl: boolean;
  allowLightningControl: boolean;
  forceControlThemeMode: 'dark' | undefined;
  withPlaces: boolean;
};

const VISUALLY_BUSY_LAYERS: MapLayer[] = [
  MapLayers.SMOKE_COVERAGE,
  MapLayers.PURPLE_AIR,
];

// TODO: These were hidden as a side effect of noControls (controlMode='hidden'),
// not intentionally disabled. Consider allowing them in form map types.
const WARNING_AND_ALERT_LAYERS: MapLayer[] = [
  MapLayers.RED_FLAG_WARNINGS,
  MapLayers.FLOOD_WEATHER,
  MapLayers.WEA_ALERTS,
];

const OWNERSHIP_AND_RESPONSIBILITY_LAYERS: MapLayer[] = [
  MapLayers.PRIVATE_LAND_OWNERSHIP,
  MapLayers.RESPONSIBILITY_AREAS,
  MapLayers.FEDERAL_STATE_LOCAL,
  MapLayers.ELECTRIC_RETAIL_SERVICE,
];

const allLayersExcept = (...excluded: MapLayer[]): MapLayer[] =>
  AllMapLayers.filter((l) => !excluded.includes(l));

// Layers that only render in timeline mode, never on the main or form maps.
const TIMELINE_ONLY_LAYERS: MapLayer[] = [MapLayers.RADAR];

// Layers that only render inside IncidentsMap
// excluded from form policy so the drawer control is shown disabled
const MAIN_MAP_ONLY_LAYERS: MapLayer[] = [
  MapLayers.WATER_LEVEL,
  MapLayers.WEATHER_STATIONS,
  MapLayers.ELECTRICAL_LINES,
  MapLayers.GAS_PIPELINES,
  MapLayers.PRIVATE_LAND_OWNERSHIP,
];

const FORM_ALLOWED_LAYERS = allLayersExcept(
  ...TIMELINE_ONLY_LAYERS,
  MapLayers.TEAM_LAYER,
  MapLayers.TEAM_POLYGON_LAYER,
  ...MAIN_MAP_ONLY_LAYERS,
  ...VISUALLY_BUSY_LAYERS,
  ...WARNING_AND_ALERT_LAYERS,
);

const STATIC_MAP_POLICY: MapTypePolicy = {
  allowedLayers: [],
  allowMainControls: false,
  allowToolsControl: false,
  allowZoomControl: false,
  allowGeolocateControl: false,
  allowLayersControl: false,
  allowTimelineControl: false,
  allowLightningControl: false,
  forceControlThemeMode: undefined,
  withPlaces: true,
};

const INCIDENT_FORM_POLICY: MapTypePolicy = {
  allowedLayers: FORM_ALLOWED_LAYERS.filter(
    (l) => l !== MapLayers.EVACUATION_ZONES,
  ),
  allowMainControls: false,
  allowToolsControl: true,
  allowZoomControl: true,
  allowGeolocateControl: false,
  allowLayersControl: true,
  allowTimelineControl: false,
  allowLightningControl: false,
  forceControlThemeMode: undefined,
  withPlaces: false,
};

const REPORT_FORM_POLICY: MapTypePolicy = {
  ...INCIDENT_FORM_POLICY,
  withPlaces: true,
};

// Flooding forms keep the flood watches and warnings carried over from the main map. The Map
// renders them non-interactively, since clicking a form map positions the incident marker.
const FLOODING_FORM_ALLOWED_LAYERS: MapLayer[] = [
  ...INCIDENT_FORM_POLICY.allowedLayers,
  MapLayers.FLOOD_WEATHER,
];

export const MAP_TYPE_POLICIES: Record<MapType, MapTypePolicy> = {
  [MapType.Main]: {
    allowedLayers: allLayersExcept(...TIMELINE_ONLY_LAYERS),
    allowMainControls: true,
    allowToolsControl: true,
    allowZoomControl: true,
    allowGeolocateControl: true,
    allowLayersControl: true,
    allowTimelineControl: true,
    allowLightningControl: true,
    forceControlThemeMode: undefined,
    withPlaces: true,
  },
  [MapType.Timeline]: {
    allowedLayers: [
      MapLayers.RADAR,
      MapLayers.EXTERNAL_GEO_EVENTS,
      MapLayers.ACTIVE_FIRE_PERIMETERS,
      MapLayers.HISTORICAL_FIRE_PERIMETERS,
      MapLayers.LIGHTNING,
      MapLayers.EVACUATION_ZONES,
      MapLayers.FEMA_FLOOD_HAZARD,
      MapLayers.TEAM_POLYGON_LAYER,
      ...WARNING_AND_ALERT_LAYERS,
      ...OWNERSHIP_AND_RESPONSIBILITY_LAYERS,
    ],
    allowMainControls: false,
    allowToolsControl: false,
    allowZoomControl: true,
    allowGeolocateControl: true,
    allowLayersControl: false,
    allowTimelineControl: false,
    allowLightningControl: false,
    forceControlThemeMode: 'dark',
    withPlaces: true,
  },
  [MapType.GenericForm]: {
    allowedLayers: FORM_ALLOWED_LAYERS,
    allowMainControls: false,
    allowToolsControl: true,
    allowZoomControl: true,
    allowGeolocateControl: false,
    allowLayersControl: true,
    allowTimelineControl: false,
    allowLightningControl: false,
    forceControlThemeMode: undefined,
    withPlaces: false,
  },
  [MapType.PlaceForm]: {
    allowedLayers: FORM_ALLOWED_LAYERS,
    allowMainControls: false,
    allowToolsControl: false,
    allowZoomControl: false,
    allowGeolocateControl: false,
    allowLayersControl: false,
    allowTimelineControl: false,
    allowLightningControl: false,
    forceControlThemeMode: undefined,
    withPlaces: false,
  },
  [MapType.FirePerimeterForm]: {
    // Hide the active fire perimeters layer. The edit form renders its own
    // instance (with a layerIdPrefix) that excludes the perimeter being edited.
    allowedLayers: FORM_ALLOWED_LAYERS.filter(
      (l) => l !== MapLayers.ACTIVE_FIRE_PERIMETERS,
    ),
    allowMainControls: false,
    allowToolsControl: false,
    allowZoomControl: true,
    allowGeolocateControl: false,
    allowLayersControl: true,
    allowTimelineControl: false,
    allowLightningControl: false,
    forceControlThemeMode: undefined,
    withPlaces: false,
  },
  [MapType.MediaForm]: {
    allowedLayers: FORM_ALLOWED_LAYERS,
    allowMainControls: false,
    allowToolsControl: false,
    allowZoomControl: false,
    allowGeolocateControl: false,
    allowLayersControl: false,
    allowTimelineControl: false,
    allowLightningControl: false,
    forceControlThemeMode: undefined,
    withPlaces: false,
  },
  [MapType.AutomatedGeoEventForm]: {
    allowedLayers: allLayersExcept(
      ...TIMELINE_ONLY_LAYERS,
      MapLayers.TEAM_LAYER,
      MapLayers.TEAM_POLYGON_LAYER,
      ...MAIN_MAP_ONLY_LAYERS,
      ...VISUALLY_BUSY_LAYERS,
    ),
    allowMainControls: true,
    allowToolsControl: true,
    allowZoomControl: true,
    allowGeolocateControl: true,
    allowLayersControl: true,
    allowTimelineControl: true,
    allowLightningControl: false,
    forceControlThemeMode: undefined,
    withPlaces: true,
  },
  [MapType.IncidentForm]: INCIDENT_FORM_POLICY,
  [MapType.FloodingForm]: {
    ...INCIDENT_FORM_POLICY,
    allowedLayers: FLOODING_FORM_ALLOWED_LAYERS,
  },
  [MapType.ReportForm]: REPORT_FORM_POLICY,
  [MapType.FloodingReportForm]: {
    ...REPORT_FORM_POLICY,
    allowedLayers: FLOODING_FORM_ALLOWED_LAYERS,
  },
  [MapType.CameraDetails]: STATIC_MAP_POLICY,
  [MapType.GeoEventEmbed]: STATIC_MAP_POLICY,
  [MapType.ReportDetail]: STATIC_MAP_POLICY,
  [MapType.EditReportForm]: STATIC_MAP_POLICY,
};
